import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import useResetAtomicState from "@/hooks/useResetAtomicState";
import { CHECKOUT_STUDENT_FLOW } from "@/ts/enums/CheckoutStudentFlowEnum";
import { LICENSE_STATUS } from "@/ts/enums/ManageProgramsEnum";
import { AddonInterface } from "@/ts/interfaces/Checkout/AddonInterface";
import { GradeInterface } from "@/ts/interfaces/Checkout/GradeInterface";
import { PlanInterface } from "@/ts/interfaces/Checkout/PlanInterface";
import { ProductFamilyInterface } from "@/ts/interfaces/Checkout/ProductFamilyInterface";
import { mapQueryParamsDataToCheckoutAtom } from "@/utils/checkoutDataMapper";
import { getTrackingId, removeNullProperties } from "@/utils/utils";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { router, usePage } from "@inertiajs/react";
import { useAtom, useSetAtom } from "jotai";

import { Flex } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { CheckoutInterface } from "@ts/interfaces/Checkout/CheckoutInterface";
import { UserInterface } from "@ts/interfaces/Parent/UserInterface";

import {
    checkoutAtom,
    checkoutStudentFlowAtom,
    isProvidedQueryParamsAtom,
    loadableProductFamiliesAtom,
    pageQueryParamsAtom
} from "@atoms/checkoutAtom";

import ContentLayout from "@layouts/ContentLayout";

import { Card } from "@components/Card";
import { Text } from "@components/Text";

const StudentSelectionContainer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [searchParams, setSearchParams] = useSearchParams();
    const setIsProvidedQueryParams = useSetAtom(isProvidedQueryParamsAtom);
    const [checkoutStudentFlow, setCheckoutStudentFlow] = useAtom(
        checkoutStudentFlowAtom
    );

    const productFamilyId = searchParams.get("family");
    const gradeName = searchParams.get("grade");
    const planId = searchParams.get("plan");
    const addonId = searchParams.get("addon");
    const coupon = searchParams.get("coupon");

    const [productFamilies] = useAtom(loadableProductFamiliesAtom);

    const { students } = usePage().props;

    const studentList = (students as UserInterface[]).filter(
        student => student.is_enrolled
    );

    const [pathNavigateTo, setPathNavigateTo] = useState(
        CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
    );

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutate, checkoutStateMutationState } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(pathNavigateTo));
        });
    const { resetCheckoutAtomicState } = useResetAtomicState();

    const isStudentSelected = !!checkout.selectedStudent;
    const username = checkout.user.firstName;

    const onGoBack = () => {
        router.get(route("parents.dashboard"));
        resetCheckoutAtomicState();
    };

    const onSaveData = () => {
        let productFamily: ProductFamilyInterface | null = null;
        let grade: GradeInterface | null = null;
        let plan: PlanInterface | null = null;
        let addon: AddonInterface | null = null;

        const pathNavigateTo =
            CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE;

        setPathNavigateTo(pathNavigateTo);

        /**
         * We try to construct the checkout state
         * from the data passed via query parameters.
         * Correctly generated checkout link with query parameter values is expected.
         */
        if (productFamilies.state === "hasData") {
            const mappedCheckoutAtom = mapQueryParamsDataToCheckoutAtom(
                {
                    addonId,
                    gradeName,
                    planId,
                    productFamilyId
                },
                productFamilies.data
            );

            productFamily = mappedCheckoutAtom.productFamily;
            grade = mappedCheckoutAtom.grade;
            plan = mappedCheckoutAtom.plan;
            addon = mappedCheckoutAtom.addon;
        }

        /**
         * Handling the default available products
         */
        let defaultAddon = addon;
        if (plan && !addon) {
            const availableAddons = plan.addons;
            if (availableAddons) {
                defaultAddon =
                    availableAddons.find(a => !a.isHighlighted) ?? null;
            }
        }
        setCheckout(prev => ({
            ...prev,
            productFamily,
            grade,
            plan,
            addon: defaultAddon,
            student: null,
            nextAttemptPage: CheckoutPagesEnum.PRODUCT_FAMILY_SELECT_PAGE
        }));

        onSetupQueryParams({
            ...pageQueryParams,
            addon: addon ? addon.id : null,
            plan: plan ? plan.id : null,
            family: productFamily ? productFamily.id : null,
            grade: grade ? grade.name : null,
            coupon
        });

        checkoutStateMutate();
    };

    const addNewStudent = {
        id: "newStudent",
        name: "",
        program: "",
        plan: ""
    };

    const handleAddNewStudent = () => {
        setCheckoutStudentFlow(CHECKOUT_STUDENT_FLOW.ADD_STUDENT_FLOW);

        const updatedCheckout: CheckoutInterface = {
            ...checkout,
            selectedStudent: null,
            nextAttemptPage: CheckoutPagesEnum.PRODUCT_FAMILY_SELECT_PAGE
        };

        setCheckout(updatedCheckout);
    };

    const getProgramAccessDate = (date: string, isExpired: boolean) => {
        let accessDate = "";

        if (isExpired) {
            accessDate = `access expired ${date}`;
        } else {
            accessDate = `access until ${date}`;
        }
        return accessDate;
    };

    const onSelectStudent = (student: UserInterface) => {
        setCheckoutStudentFlow(
            CHECKOUT_STUDENT_FLOW.EXISTING_STUDENT_SELECTION_FLOW
        );

        const updatedCheckout: CheckoutInterface = {
            ...checkout,
            selectedStudent: student
        };

        setCheckout(updatedCheckout);
    };

    const getPlan = (type: string) => {
        if (type === "yearly") {
            return "Full year";
        } else if (type === "installment") {
            return "Full year (instalment)";
        }
        return "One term";
    };

    const getProgramStatus = (status: string) => {
        if (
            status === LICENSE_STATUS.ACTIVE ||
            status === LICENSE_STATUS.NON_RENEWING
        ) {
            return "Active";
        }
        return "Not active";
    };

    useEffect(() => {
        // Set all the query parameters in the URL which were updated in "pageQueryParams" atom at this point.
        setSearchParams(removeNullProperties(pageQueryParams));
        setIsProvidedQueryParams(false);
    }, [pageQueryParams]);

    return (
        <ContentLayout
            className={ClassesEnum.StudentSelectionContainer}
            title={t(
                "checkout.selectStudentPage.title",
                "Hi {{username}}, which student are you buying new content for?",
                { username }
            )}
            footerActionsProps={{
                canGoNext:
                    isStudentSelected ||
                    checkoutStudentFlow ===
                        CHECKOUT_STUDENT_FLOW.ADD_STUDENT_FLOW,
                isNextBtnLoading: checkoutStateMutationState.isPending,
                onGoBack,
                onGoNext: onSaveData,
                btnClassHierarchy: {
                    next: [
                        ClassesEnum.StudentSelectionContainer,
                        ClassesEnum.Button
                    ],
                    back: [
                        ClassesEnum.StudentSelectionContainer,
                        ClassesEnum.Button
                    ]
                },
                btnActions: {
                    next: "goNextFromStudentSelectPage",
                    back: "goBackFromStudentSelectPage"
                }
            }}
            containerWidth={["95%", null, null, null, 950]}
            step={2}
        >
            <Flex direction="column" gap={8} align="center">
                {studentList &&
                    studentList.length > 0 &&
                    studentList.map(s => {
                        return (
                            <Card<UserInterface>
                                classHierarchy={[
                                    ClassesEnum.StudentSelectionContainer,
                                    ClassesEnum.Card
                                ]}
                                action={`selectStudent-${s.id}`}
                                key={s.id}
                                item={s}
                                isActive={
                                    checkout.selectedStudent?.id === s.id &&
                                    checkoutStudentFlow ===
                                        CHECKOUT_STUDENT_FLOW.EXISTING_STUDENT_SELECTION_FLOW
                                }
                                onClick={onSelectStudent}
                                width={560}
                                height="auto"
                                cardBorderRadius="24px"
                            >
                                <Flex
                                    gap={2}
                                    h="full"
                                    w="full"
                                    id={`student-${s.id}`}
                                    direction="column"
                                    data-testid={`student-${s.id}`}
                                >
                                    <Flex direction="column">
                                        <Text
                                            type="subHeading"
                                            lineHeight="25px"
                                            data-tracking-id={getTrackingId(
                                                [
                                                    ClassesEnum.StudentSelectionContainer,
                                                    ClassesEnum.Card,
                                                    ClassesEnum.Text
                                                ],
                                                `studentName-${s.id}`
                                            )}
                                        >
                                            {s.name}
                                        </Text>
                                        <Text
                                            type="normal"
                                            fontFamily="inter, sans-serif"
                                            color="gray.500"
                                            fontSize={12}
                                            lineHeight="20px"
                                        >
                                            Student ID - {s.auth0_username}
                                        </Text>
                                    </Flex>
                                    <Flex direction="column">
                                        <Text
                                            type="normal"
                                            fontFamily="inter, sans-serif"
                                            fontSize={14}
                                            color="gray.800"
                                            lineHeight="23px"
                                            data-testid={`program-${s.id}`}
                                        >
                                            Program:{" "}
                                            <span style={{ fontWeight: 700 }}>
                                                {
                                                    s.latest_license?.plan
                                                        .families.name
                                                }{" "}
                                                ({s.student_grade?.long_name})
                                            </span>
                                        </Text>
                                        <Text
                                            type="normal"
                                            fontFamily="inter, sans-serif"
                                            fontSize={14}
                                            color="gray.800"
                                            lineHeight="23px"
                                            data-testid={`plan-type-${s.id}`}
                                        >
                                            Plan:{" "}
                                            <span style={{ fontWeight: 700 }}>
                                                {getPlan(
                                                    s.latest_license?.plan
                                                        .plan_type?.type
                                                )}
                                            </span>
                                        </Text>
                                        <Text
                                            type="normal"
                                            fontFamily="inter, sans-serif"
                                            fontSize={14}
                                            color="gray.800"
                                            lineHeight="23px"
                                            data-testid={`program-status-${s.id}`}
                                        >
                                            Program status:{" "}
                                            <span style={{ fontWeight: 700 }}>
                                                {getProgramStatus(
                                                    s.latest_license
                                                        ?.license_status
                                                )}{" "}
                                                (
                                                {getProgramAccessDate(
                                                    s.license_expiry_at_text,
                                                    s.has_expired_license
                                                )}
                                                )
                                            </span>
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Card>
                        );
                    })}
                <Card
                    classHierarchy={[
                        ClassesEnum.StudentSelectionContainer,
                        ClassesEnum.Card
                    ]}
                    action="addStudent"
                    key={addNewStudent.id}
                    item={addNewStudent}
                    onClick={handleAddNewStudent}
                    width={560}
                    height="auto"
                    cardBorderRadius="24px"
                    isActive={
                        checkoutStudentFlow ===
                        CHECKOUT_STUDENT_FLOW.ADD_STUDENT_FLOW
                    }
                >
                    <Flex h="full" w="full" direction="column">
                        <Flex direction="row" align="center">
                            <Flex
                                borderRadius="50%"
                                border="2px solid #27272E"
                                width="24px"
                                height="24px"
                                alignItems="center"
                                justifyContent="center"
                                mr={3}
                                direction="column"
                            >
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    color="#27272E"
                                />
                            </Flex>
                            <Text
                                type="subHeading"
                                color="gray.800"
                                data-tracking-id={getTrackingId(
                                    [
                                        ClassesEnum.StudentSelectionContainer,
                                        ClassesEnum.Card,
                                        ClassesEnum.Text
                                    ],
                                    "addNewStudent"
                                )}
                            >
                                Add a new student
                            </Text>
                        </Flex>
                    </Flex>
                </Card>
            </Flex>
        </ContentLayout>
    );
};

export default StudentSelectionContainer;
